import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { resolvedMode } from '@/theme';

/** 토스트도 현재 밝기 모드를 따른다. */
function useThemeKind(): 'light' | 'dark' {
  const [kind, setKind] = useState<'light' | 'dark'>(resolvedMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setKind(resolvedMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-mode'],
    });
    return () => observer.disconnect();
  }, []);

  return kind;
}

function Toaster({ ...props }: ToasterProps) {
  const kind = useThemeKind();

  return (
    <Sonner
      theme={kind}
      // **상단 가운데**다(2026-09-17 지시). 그전에는 오른쪽 위였다 —
      // 아래쪽에 두면 좁은 화면에서 도구줄·단추와 겹친다는 것은 그대로다.
      //
      // 이 자리가 곧 애니메이션이다. sonner 는 `top` 이면 토스트를
      // `translateY(-100%)` 에서 시작해 `0` 으로 **내리고**, 사라질 때 다시
      // 위로 **올린다**(`sonner/dist/styles.css`). 그래서 「내려오고
      // 올라간다」에 우리 CSS 를 한 줄도 안 쓴다 — 쓰면 판본을 올릴 때
      // 저쪽 규칙과 어긋난다.
      position="top-center"
      // ⚠ **GNB 를 덮지 않게 내려 잡는다.** 기본 offset 으로 두면 가운데 칸의
      // 검색칸 위에 5초 동안 얹힌다. 값의 주인은 `styles.css` 의
      // `--toast-top` 이고 좁은 화면(GNB 가 두 줄)에서 그쪽이 늘려 준다.
      //
      // ⚠ **sonner 의 모바일 기준은 600px 인데 우리 GNB 는 720px 에서 접힌다.**
      // 그래서 둘 다 같은 변수를 보게 둔다 — 한쪽만 주면 그 사이 120px 구간에서
      // 토스트가 검색줄을 가린다.
      offset={{ top: 'var(--toast-top)' }}
      mobileOffset={{ top: 'var(--toast-top)', left: 'var(--s4)', right: 'var(--s4)' }}
      // **5초다.** sonner 기본값 4초는 한글 한 문장을 읽기에 빠듯하다 —
      // 「viewster-118 만들었다」처럼 번호가 앞에 붙으면 더 그렇다.
      duration={5000}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          // 색은 **우리 토큰**에서 온다. sonner 기본값을 쓰면 테마 일곱 중
          // 어디에서도 안 맞는다.
          '--normal-bg': 'var(--panel)',
          '--normal-text': 'var(--ink)',
          '--normal-border': 'var(--line-2)',
          '--border-radius': 'var(--r)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
}

export { Toaster };
